/**
 * studentSpaceBridge — le pont d'une route poussée de l'espace élève, posé dans
 * `navigationScreens.js` une fois pour toutes par le socle.
 *
 * Il rend l'écran que le lot a inscrit pour cette route (`studentSpaceRoutes`) quand trois choses
 * sont vraies : le compte courant est un ÉLÈVE (le tuteur garde toujours son legacy, même sur une route qu'il partage : le live, l'entrée, la
 * fin), on n'est pas en mode parent (une route partagée avec le parent reste la sienne :
 * `parentOnlyBridge` le dit pour l'autre sens), et — seulement pour une entrée qui ne déclare pas
 * vivre en live (`livesInLive`) — on n'est pas en live. Sinon, ou tant que le lot n'a rien inscrit,
 * l'écran legacy, avec ses props intactes.
 *
 * Comme les autres ponts (`featureFlags/screenBridge.js`) : aucun crochet avant la branche — la
 * condition arrive par `connect`, ce qui redessine aussi le pont quand elle change —, et l'écran des
 * ports ne reçoit que `route`. Il est monté dans `StudentSpaceScreenHost`, qui retire l'en-tête de
 * la pile : une page du design system porte le sien.
 */
import React, { useLayoutEffect } from "react"
import { useNavigation } from "@react-navigation/native"
import { connect } from "react-redux"

import {
    readRouteEntry,
    type StudentSpaceRouteEntry,
    type StudentSpaceRouteName,
    type StudentSpaceScreen,
    type StudentSpaceScreenProps,
} from "./studentSpaceRouteTypes"
import { selectAccount } from "../../../view-models/shared/displayedStudent"

/** Le registre, chargé à la première route allumée seulement (voir le `require` plus bas). */
type StudentSpaceRoutesModule = typeof import("./studentSpaceRoutes")
type ThemeSettledScreenModule = typeof import("./ThemeSettledScreen")

type BridgeState =
    | {
          data?: {
              family?: { isParentMode?: boolean } | null
          }
          liveMode?: { isLiveModeEnabled?: boolean }
      }
    | undefined

/** Ce que le pont lit du store : l'espace élève est-il celui de ce compte, et le live est-il ouvert ? */
export type StudentSpaceContext = { on: boolean; inLive: boolean }

/**
 * Un élève, et pas un tuteur : la même lecture que `LiveMainLayout`, où le rôle de tuteur l'emporte
 * (un compte qui porterait les deux garde le live du tuteur).
 */
function isStudentAccount(roles: unknown): boolean {
    return Array.isArray(roles) && roles.includes("student") && !roles.includes("tutor")
}

/** La lecture du pont, pure : élève, hors mode parent ; et le live. */
export function selectStudentSpaceContext(state: unknown): StudentSpaceContext {
    const root = state as BridgeState
    const on = isStudentAccount(selectAccount(root)?.roles) && !root?.data?.family?.isParentMode
    return { on, inLive: !!root?.liveMode?.isLiveModeEnabled }
}

/**
 * La règle du pont, pure : l'espace élève du design system est-il celui de cette route, maintenant ?
 * `livesInLive` : l'entrée inscrite déclare vivre en live (`livesInLive(screen)`).
 */
export function isStudentSpaceRouteOn(state: unknown, livesInLive = false): boolean {
    return routeOpens(selectStudentSpaceContext(state), livesInLive)
}

function routeOpens(context: StudentSpaceContext, livesInLive: boolean): boolean {
    return context.on && (!context.inLive || livesInLive)
}

/** L'écran que le pont rend pour une entrée du registre, ou `undefined` : le legacy. */
export function studentSpaceScreenFor(
    entry: StudentSpaceRouteEntry | undefined,
    context: StudentSpaceContext
): StudentSpaceScreen | undefined {
    if (entry == null) return undefined
    const { screen, inLive } = readRouteEntry(entry)
    return routeOpens(context, inLive) ? screen : undefined
}

type StudentSpaceScreenHostProps = StudentSpaceScreenProps & {
    Screen: React.ComponentType<StudentSpaceScreenProps>
}

function StudentSpaceScreenHost(props: StudentSpaceScreenHostProps) {
    const { Screen, route } = props
    const navigation = useNavigation() as unknown as { setOptions: (options: object) => void }

    useLayoutEffect(() => {
        navigation.setOptions({ headerShown: false })
    }, [navigation])

    // Le thème de l'élève posé avant la première peinture, même ouvert à froid (`ThemeSettledScreen`).
    // `require` paresseux, comme le registre : éteint, rien de l'espace élève n'est évalué.
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const { ThemeSettledScreen } = require("./ThemeSettledScreen") as ThemeSettledScreenModule
    return <ThemeSettledScreen Screen={Screen} route={route} />
}

type BridgeProps = {
    /** L'écran des ports à rendre, lu sur le store ; `undefined` : le legacy. */
    portsScreen: StudentSpaceScreen | undefined
    // Le sac de props legacy (navigation, route, et ce que la pile y ajoute) : relayé tel quel.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [legacyProp: string]: any
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function studentSpaceBridge(routeName: StudentSpaceRouteName, LegacyScreen: React.ComponentType<any>) {
    const Bridge = (props: BridgeProps) => {
        const { portsScreen: Screen, ...legacyProps } = props
        return Screen ? (
            <StudentSpaceScreenHost Screen={Screen} route={legacyProps.route} />
        ) : (
            <LegacyScreen {...legacyProps} />
        )
    }
    Bridge.displayName = `StudentSpaceBridge(${routeName})`

    // `{}` en second argument : pas de `dispatch` glissé dans les props du legacy.
    // L'écran se choisit sur le store (le compte, le mode parent, le live) : `connect` redessine le
    // pont quand il change. `require` paresseux : pour un tuteur ou un parent, ni le registre ni les
    // écrans élève qu'il importe ne sont évalués.
    return connect((state: unknown) => {
        const context = selectStudentSpaceContext(state)
        const entry = context.on
            ? // eslint-disable-next-line @typescript-eslint/no-require-imports
              (require("./studentSpaceRoutes") as StudentSpaceRoutesModule).studentSpaceRoutes[routeName]
            : undefined
        return { portsScreen: studentSpaceScreenFor(entry, context) }
    }, {})(Bridge)
}
